(() => {
    const WORD_NUMBERS = {
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        five: 5,
        six: 6,
        seven: 7,
        eight: 8,
        nine: 9,
        ten: 10,
        eleven: 11,
        twelve: 12,
        thirteen: 13,
        fourteen: 14,
        fifteen: 15,
        sixteen: 16,
        seventeen: 17,
        eighteen: 18,
        nineteen: 19,
        twenty: 20,
    };

    const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
    });

    const normalizeText = (value) => String(value || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const normalizeHandle = (value) => String(value || '')
        .toLowerCase()
        .trim();

    const tokenToNumber = (token) => {
        const clean = String(token || '').toLowerCase();
        if (/^\d+$/.test(clean)) {
            return Number.parseInt(clean, 10);
        }
        return WORD_NUMBERS[clean] || 0;
    };

    const sequenceNumbers = (title) => {
        const matches = [];
        const pattern = /\b(?:season|part|set|volume|vol\.?|collection|book)\s*(?:no\.?|#)?\s*(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\b/gi;

        for (const match of String(title || '').matchAll(pattern)) {
            const number = tokenToNumber(match[1]);
            if (number > 0) {
                matches.push(number);
            }
        }

        return matches;
    };

    const fallbackFamilyKey = (title) => normalizeText(title)
        .replace(/\b(?:blu ray|bluray|dvd|uhd|4k|bundle|complete series|complete collection|collection|essentials|classic|classics)\b/g, ' ')
        .replace(/\b(?:season|part|set|volume|vol|collection|book)\s*(?:no|#)?\s*(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const cardInfo = (card) => {
        const title = card.dataset.nubsCatalogTitle || '';
        const universe = normalizeText(card.dataset.nubsCatalogUniverse || '');
        const isBundle = card.dataset.nubsCatalogBundle === 'true';
        const seasonValues = String(card.dataset.nubsCatalogSeason || '')
            .split(',')
            .map((value) => Number.parseInt(value.trim(), 10) || 0)
            .filter((value) => value > 0);
        const seasonNumber = seasonValues[0] || 0;
        const scopeType = normalizeText(card.dataset.nubsCatalogScopeType || '');
        const scopeNumber = Number.parseInt(card.dataset.nubsCatalogScopeNumber || '', 10) || 0;
        const releaseType = normalizeText(card.dataset.nubsCatalogReleaseType || '');
        const numbers = sequenceNumbers(title);
        const lowerTitle = normalizeText(title);
        const bundleContents = String(card.dataset.nubsCatalogBundleContents || '')
            .split(',')
            .map(normalizeHandle)
            .filter(Boolean);

        let order = 0;

        if (!isBundle && (seasonNumber > 0 || scopeNumber > 0)) {
            const primary = seasonNumber > 0 ? seasonNumber : scopeNumber;
            const secondary = seasonNumber > 0 ? scopeNumber : 0;
            order = (primary * 10000) + (secondary * 100);
        } else if (numbers.length > 0) {
            if (isBundle) {
                const highest = Math.max(...numbers);
                // Bundle follows the highest numbered release that it contains.
                order = (highest * 10000) + 5000;
            } else {
                const first = numbers[0] || 0;
                const second = numbers[1] || 0;
                const third = numbers[2] || 0;
                order = (first * 10000) + (second * 100) + third;
            }
        } else if (/\b(?:ova|oad|special|specials)\b/.test(releaseType) || /\b(?:ova|oad|special|specials)\b/.test(lowerTitle)) {
            order = 850000;
        } else if (/\b(?:movie|film|theatrical)\b/.test(releaseType) || /\b(?:movie|film|theatrical)\b/.test(lowerTitle)) {
            order = 900000;
        } else if (isBundle) {
            order = 950000;
        }

        return {
            card,
            title,
            handle: normalizeHandle(card.dataset.nubsCatalogHandle || ''),
            universe,
            seasonNumber,
            scopeType,
            scopeNumber,
            releaseType,
            family: universe || fallbackFamilyKey(title),
            isBundle,
            bundleContents,
            order,
        };
    };

    const applyExplicitBundlePlacement = (info) => {
        const byHandle = new Map();

        for (const item of info) {
            if (item.handle) {
                byHandle.set(item.handle, item);
            }
        }

        for (const item of info) {
            if (!item.isBundle || item.bundleContents.length === 0) {
                continue;
            }

            const components = item.bundleContents
                .map((handle) => byHandle.get(handle))
                .filter(Boolean);

            if (components.length === 0) {
                continue;
            }

            const families = Array.from(new Set(
                components
                    .map((component) => component.family)
                    .filter(Boolean)
            ));

            // Only regroup explicit bundles when their visible components share one family.
            // Cross-series bundles are still shown normally, while component product pages use
            // custom.available_bundles for the green offer card.
            if (families.length === 1) {
                item.family = families[0];
            }

            const highestComponentOrder = Math.max(
                ...components.map((component) => Number(component.order) || 0)
            );

            if (Number.isFinite(highestComponentOrder)) {
                const explicitOrder = highestComponentOrder + 5000;
                if (item.order === 0 || item.order >= 950000 || explicitOrder > item.order) {
                    item.order = explicitOrder;
                }
            }
        }
    };

    const compareCards = (a, b) => {
        if (a.order !== b.order) {
            return a.order - b.order;
        }

        if (a.isBundle !== b.isBundle) {
            return a.isBundle ? 1 : -1;
        }

        return collator.compare(a.title, b.title);
    };

    const revealGrid = (grid) => {
        if (grid instanceof HTMLElement) {
            grid.removeAttribute('data-nubs-catalog-order-pending');
        }
    };

    const normalizeGrid = (grid, selector = '[data-nubs-catalog-card]') => {
        if (!(grid instanceof HTMLElement)) {
            return;
        }

        try {
            const cards = Array.from(grid.children)
                .filter((node) => node instanceof HTMLElement && node.matches(selector));

            if (cards.length < 2) {
                return;
            }

            const info = cards.map(cardInfo);
            applyExplicitBundlePlacement(info);

            const groups = new Map();

            for (const item of info) {
                const key = item.family;
                if (!key) {
                    continue;
                }

                if (!groups.has(key)) {
                    groups.set(key, []);
                }

                groups.get(key).push(item);
            }

            const emitted = new Set();
            const fragment = document.createDocumentFragment();

            for (const item of info) {
                const key = item.family;
                const group = key ? groups.get(key) : null;

                if (!group || group.length < 2) {
                    fragment.appendChild(item.card);
                    continue;
                }

                if (emitted.has(key)) {
                    continue;
                }

                emitted.add(key);
                group.sort(compareCards);

                for (const member of group) {
                    fragment.appendChild(member.card);
                }
            }

            grid.appendChild(fragment);
        } finally {
            revealGrid(grid);
        }
    };

    const shouldUseDefaultOrdering = () => {
        const params = new URLSearchParams(window.location.search);
        return !params.has('sort_by');
    };

    window.NubsCatalogOrder = {
        normalizeGrid,
        revealGrid,
        shouldUseDefaultOrdering,
    };
})();
